import { useRef } from 'react';

import {
  InboxEntryReplyStatePayload,
  WebSocketEntityType,
} from '@this+that/models';
import log from 'loglevel';

import { useEntitySync } from './useEntitySync';
import { useInboxEntriesStore } from '../../../store';

const buildThreadStateKey = (item: InboxEntryReplyStatePayload): string =>
  `${item.inboxEntryId}#${item.threadMessageId}`;

/** Pushes for one thread can arrive reordered; only a payload at least as recent
 *  as the last applied one may overwrite the thread's reply state. */
export function isFreshReplyState(
  appliedAt: Map<string, number>,
  item: InboxEntryReplyStatePayload,
): boolean {
  const previous = appliedAt.get(buildThreadStateKey(item));
  return previous === undefined || item.computedAt >= previous;
}

export function useInboxEntryReplyStateSync() {
  const applyInboxEntryReplyState = useInboxEntriesStore(
    (s) => s.taskManager.activeTaskGroup.applyInboxEntryReplyState,
  );
  const appliedAtRef = useRef(new Map<string, number>());

  const apply = (item: InboxEntryReplyStatePayload) => {
    if (!item.inboxEntryId || !item.threadMessageId) {
      log.warn('[InboxEntryReplyStateSync] Received incomplete payload', item);
      return;
    }
    if (!isFreshReplyState(appliedAtRef.current, item)) {
      return;
    }
    appliedAtRef.current.set(buildThreadStateKey(item), item.computedAt);
    applyInboxEntryReplyState(item);
  };

  useEntitySync<InboxEntryReplyStatePayload>(
    WebSocketEntityType.InboxEntryReplyState,
    { onInsert: apply, onModify: apply },
  );
}
